import { Box, Modal, IconButton, Typography, useTheme, useMediaQuery } from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { useState } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';

export const ChatImageGallery = ({ images, isOwn }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  if (!images || images.length === 0) return null;

  const handleOpenLightbox = (index) => {
    setActiveIndex(index);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
  };

  const handlePrevious = (e) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleDownload = () => {
    const image = images[activeIndex];
    const url = image.temporaryUrl || image.thumbnailUrl;
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') handlePrevious(e);
    if (e.key === 'ArrowRight') handleNext(e);
    if (e.key === 'Escape') handleCloseLightbox();
  };

  // Single image - full preview
  if (images.length === 1) {
    return (
      <Box
        onClick={(e) => {
          e.stopPropagation();
          handleOpenLightbox(0);
        }}
        sx={{
          maxWidth: isMobile ? 200 : 280,
          cursor: 'pointer',
          borderRadius: '8px',
          overflow: 'hidden',
          '&:hover': { opacity: 0.9 },
        }}
      >
        <img
          src={images[0].temporaryUrl || images[0].thumbnailUrl}
          alt={images[0].title || 'Image'}
          style={{
            width: '100%',
            height: 'auto',
            maxHeight: isMobile ? 200 : 280,
            objectFit: 'cover',
            display: 'block',
          }}
        />
        <Lightbox
          open={lightboxOpen}
          onClose={handleCloseLightbox}
          images={images}
          activeIndex={activeIndex}
          setActiveIndex={setActiveIndex}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onDownload={handleDownload}
          onKeyDown={handleKeyDown}
          colors={colors}
          isMobile={isMobile}
          t={t}
        />
      </Box>
    );
  }

  // Two images - side by side
  if (images.length === 2) {
    return (
      <Box
        className="flex gap-1"
        sx={{ maxWidth: isMobile ? 220 : 300 }}
        onClick={(e) => e.stopPropagation()}
      >
        {images.map((image, index) => (
          <Box
            key={image.id}
            onClick={() => handleOpenLightbox(index)}
            sx={{
              flex: 1,
              cursor: 'pointer',
              borderRadius: '8px',
              overflow: 'hidden',
              '&:hover': { opacity: 0.9 },
            }}
          >
            <img
              src={image.temporaryUrl || image.thumbnailUrl}
              alt={image.title || 'Image'}
              style={{
                width: '100%',
                height: isMobile ? 100 : 130,
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </Box>
        ))}
        <Lightbox
          open={lightboxOpen}
          onClose={handleCloseLightbox}
          images={images}
          activeIndex={activeIndex}
          setActiveIndex={setActiveIndex}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onDownload={handleDownload}
          onKeyDown={handleKeyDown}
          colors={colors}
          isMobile={isMobile}
          t={t}
        />
      </Box>
    );
  }

  // 3+ images - grid gallery
  const displayImages = images.slice(0, 4);
  const remainingCount = images.length - 4;

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <Box
        className="grid gap-1"
        sx={{
          gridTemplateColumns: 'repeat(2, 1fr)',
          maxWidth: isMobile ? 180 : 240,
        }}
      >
        {displayImages.map((image, index) => (
          <Box
            key={image.id}
            onClick={() => handleOpenLightbox(index)}
            sx={{
              position: 'relative',
              cursor: 'pointer',
              borderRadius: '6px',
              overflow: 'hidden',
              aspectRatio: '1',
              '&:hover': { opacity: 0.9 },
            }}
          >
            <img
              src={image.temporaryUrl || image.thumbnailUrl}
              alt={image.title || 'Image'}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
            {/* Show overlay with remaining count on the 4th image */}
            {index === 3 && remainingCount > 0 && (
              <Box
                className="absolute inset-0 flex items-center justify-center"
                sx={{
                  backgroundColor: 'rgba(0,0,0,0.6)',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                  }}
                >
                  +{remainingCount}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>

      <Lightbox
        open={lightboxOpen}
        onClose={handleCloseLightbox}
        images={images}
        activeIndex={activeIndex}
        setActiveIndex={setActiveIndex}
        onPrevious={handlePrevious}
        onNext={handleNext}
        onDownload={handleDownload}
        onKeyDown={handleKeyDown}
        colors={colors}
        isMobile={isMobile}
        t={t}
      />
    </Box>
  );
};

// Lightbox Modal Component
const Lightbox = ({ open, onClose, images, activeIndex, setActiveIndex, onPrevious, onNext, onDownload, onKeyDown, colors, isMobile, t }) => {
  if (!open) return null;

  const currentImage = images[activeIndex];

  return (
    <Modal
      open={open}
      onClose={onClose}
      onKeyDown={onKeyDown}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        onClick={onClose}
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.95)',
          outline: 'none',
        }}
      >
        {/* Header */}
        <Box
          className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3"
          sx={{ zIndex: 10 }}
        >
          <Typography sx={{ color: 'white', opacity: 0.8 }}>
            {activeIndex + 1} / {images.length}
          </Typography>
          <Box className="flex items-center gap-1">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onDownload();
              }}
              sx={{ color: 'white', opacity: 0.8, '&:hover': { opacity: 1 } }}
            >
              <DownloadIcon />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              sx={{ color: 'white', opacity: 0.8, '&:hover': { opacity: 1 } }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Navigation - Previous */}
        {images.length > 1 && (
          <IconButton
            onClick={onPrevious}
            sx={{
              position: 'absolute',
              left: isMobile ? 8 : 24,
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.4)',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' },
              zIndex: 10,
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: isMobile ? 28 : 36 }} />
          </IconButton>
        )}

        {/* Main Image */}
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            maxWidth: '90%',
            maxHeight: '80%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={currentImage?.temporaryUrl || currentImage?.thumbnailUrl}
            alt={currentImage?.title || 'Image'}
            style={{
              maxWidth: '100%',
              maxHeight: '80vh',
              objectFit: 'contain',
              borderRadius: '8px',
            }}
          />
        </Box>

        {/* Navigation - Next */}
        {images.length > 1 && (
          <IconButton
            onClick={onNext}
            sx={{
              position: 'absolute',
              right: isMobile ? 8 : 24,
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.4)',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' },
              zIndex: 10,
            }}
          >
            <ChevronRightIcon sx={{ fontSize: isMobile ? 28 : 36 }} />
          </IconButton>
        )}

        {/* Thumbnail strip */}
        {images.length > 1 && (
          <Box
            className="absolute bottom-4 flex gap-2 px-4"
            onClick={(e) => e.stopPropagation()}
            sx={{
              overflowX: 'auto',
              maxWidth: '90%',
              pb: 1,
            }}
          >
            {images.map((image, index) => (
              <Box
                key={image.id}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveIndex(index);
                }}
                sx={{
                  width: isMobile ? 48 : 60,
                  height: isMobile ? 48 : 60,
                  borderRadius: '6px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  flexShrink: 0,
                  opacity: index === activeIndex ? 1 : 0.5,
                  border: index === activeIndex ? `2px solid ${colors.info}` : '2px solid transparent',
                  transition: 'all 0.2s',
                  '&:hover': { opacity: 1 },
                }}
              >
                <img
                  src={image.temporaryUrl || image.thumbnailUrl}
                  alt={image.title || 'Thumbnail'}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default ChatImageGallery;
